import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core'
import { AsyncPipe } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { firstValueFrom, map, Observable } from 'rxjs'
import { AvatarComponent } from '../../avatar/avatar.component'
import { Call, CallsService } from '../../calls.service'
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component'
import { ToastService } from '../../toast.service'
import { UserNamePipe } from '../../user-name.pipe'
import { User, UsersService } from '../../users.service'
import { constructNameForUser } from '../../utils'

@Component({
  selector: 'app-call-users-card',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    MatAutocompleteModule,
    MatButton,
    MatIconButton,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatTooltip,
    TranslatePipe,
    AvatarComponent,
    UserNamePipe,
  ],
  templateUrl: './call-users-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './call-users-card.component.scss',
})
export class CallUsersCardComponent implements OnInit {
  @Input({ required: true })
  call!: Call

  private callsService = inject(CallsService)
  private usersService = inject(UsersService)
  private toastService = inject(ToastService)
  private translateService = inject(TranslateService)
  private dialog = inject(MatDialog)

  addCallUserMutation = this.callsService.addCallUserMutation()
  removeCallUserMutation = this.callsService.removeCallUserMutation()

  currentUserId = this.usersService.currentUser$.value?.id ?? null

  /** The autocomplete's text, which matches one option exactly once a user has been picked. */
  filter = ''

  organizationUsers$!: Observable<User[]>

  ngOnInit() {
    // A support user's own organization has nothing to do with the meeting, so they are offered the
    // people of the meeting's organization instead.
    this.organizationUsers$ =
      this.usersService.currentUser$.value?.role === 'SUPPORT'
        ? this.usersService.getUsersByOrganizationId(this.call.organizationId).pipe(map((result) => result.data?.list ?? []))
        : this.usersService.getOrganizationUsers().pipe(map((result) => result.data ?? []))
  }

  get canManage(): boolean {
    return this.call.accessLevel === 'FULL_ACCESS'
  }

  get isLastCallUser(): boolean {
    return this.call.users.length === 1
  }

  addableUsers(organizationUsers: User[]): User[] {
    const filter = this.filter.toLowerCase()
    return organizationUsers
      .filter((user) => !this.call.users.some((callUser) => callUser.id === user.id))
      .filter((user) => filter === '' || this.describeUser(user).toLowerCase().includes(filter))
  }

  selectedUser(organizationUsers: User[]): User | null {
    return this.addableUsers(organizationUsers).find((user) => this.describeUser(user) === this.filter) ?? null
  }

  // Doubles as the option's value, so that the picked option is recognisable from the text alone.
  describeUser(user: User): string {
    return `${constructNameForUser(user)} (${user.email})`
  }

  async addCallUser(user: User | null) {
    if (user === null) {
      return
    }
    try {
      await this.addCallUserMutation.mutateAsync({ callId: this.call.id, userId: user.id })
      this.filter = ''
      this.toastService.show('CALL_SETUP.CALL_USERS.ADDED')
    } catch {
      this.toastService.show('CALL_SETUP.CALL_USERS.ADD_ERROR')
    }
  }

  async removeCallUser(user: User) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALL_SETUP.CALL_USERS.REMOVE_DIALOG.TITLE'),
        description: this.translateService.instant('CALL_SETUP.CALL_USERS.REMOVE_DIALOG.DESCRIPTION', { userName: constructNameForUser(user) }),
      },
    })
    const confirmed = await firstValueFrom(dialogRef.afterClosed())
    if (confirmed !== true) {
      return
    }
    try {
      await this.removeCallUserMutation.mutateAsync({ callId: this.call.id, userId: user.id })
      this.toastService.show('CALL_SETUP.CALL_USERS.REMOVED')
    } catch {
      this.toastService.show('CALL_SETUP.CALL_USERS.REMOVE_ERROR')
    }
  }
}
